import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import SearchTrust from '@justpark/ui/src/components/SearchTrust';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import SearchForm from '@justpark/ui/src/components/SearchForm';
import Button from '@justpark/ui/src/components/Button';
import { useLazyQuery } from '@apollo/client';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import localDateTimeIso8601 from '@justpark/helpers/src/localDateTimeIso8601/localDateTimeIso8601';
import { defaultSearchDates } from '../../helpers/date';
import { gtagTrack } from '../../helpers/Tracking/Analytics';
import GET_SEARCH from '../../queries/search/getSearch.graphql';
import { selectLanguage } from '../../stores/partner';
import { setGeolocation } from '../../stores/geolocation';
import { T } from '../../types';
import FavouriteSearches from '../Home/FavouriteSearches';
import handleFamiliarSpaces from '../../helpers/handleFamiliarSpaces';
import useLocalStorage from '../../hooks/useLocalStorage';
import useDarkly from '../../hooks/useDarkly';

import styles from './searchFormModule.module.scss';

dayjs.extend(dayjsDuration);

type Props = {
  searchInputRef: any,
  defaultLocation?: {
    lat: number,
    lng: number
  },
  label: string | T,
  placeholder: string | T,
  submitText?: string,
  timezone: string,
  isDriveup?: boolean,
  showFamiliarSpaces?: boolean
};

const cypress = 'search-form';
const SearchFormModule = ({
  searchInputRef,
  defaultLocation,
  label,
  placeholder,
  submitText,
  timezone,
  isDriveup,
  showFamiliarSpaces = false
}: Props) => {
  const locale = useSelector(selectLanguage);
  const { t } = useTranslation();
  const defaultDates = defaultSearchDates(timezone, locale);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const monthlyParam = searchParams?.get('monthly');
  const isMonthlyDefault =
    monthlyParam === 'STANDARD' || monthlyParam === 'WEEKDAYS_ONLY';

  const [searchData, setSearchData] = useState({
    query: '',
    bookingType: isMonthlyDefault ? 'monthly' : 'hourly',
    arriving: defaultDates.arriving.format(localDateTimeIso8601),
    leaving: defaultDates.leaving.format(localDateTimeIso8601),
    monthlyStarting: dayjs(defaultDates.arriving)
      .add(2, 'days')
      .format(localDateTimeIso8601),
    monthlyDuration: monthlyParam === 'STANDARD' ? 'STANDARD' : 'WEEKDAYS_ONLY',
    searchData: defaultLocation || {}
  });

  const [recentSearches, setRecentSearches] = useLocalStorage(
    'jp_recentSearches',
    []
  );
  const [favourites, setFavourites] = useLocalStorage('jp_favourites', []);

  const navigate = useNavigate();

  const [showValidation, setShowValidation] = useState(false);

  const isValid = useMemo(() => {
    const {
      searchData: { lat, lng }
    } = searchData;

    return defaultLocation !== undefined || (lat && lng);
  }, [searchData, defaultLocation]);

  const isFormComplete = useMemo(() => {
    const {
      bookingType,
      arriving,
      leaving,
      monthlyStarting,
      monthlyDuration,
      searchData: { lat, lng }
    } = searchData;

    if (bookingType === 'hourly') {
      return Boolean(arriving && leaving && lat && lng);
    }

    return Boolean(monthlyStarting && monthlyDuration && lat && lng);
  }, [searchData]);

  const coords = useMemo(() => {
    const {
      searchData: { lat, lng }
    } = searchData;

    if (lat && lng) {
      return {
        lat,
        lng
      };
    }

    return null;
  }, [searchData]);

  const filterQueryString = useMemo(() => {
    const { arriving } = searchData;
    const arrivingDate = dayjs(arriving);
    const leadTime = dayjs.duration(arrivingDate.diff(dayjs())).asMinutes();
    return leadTime > 30 ? 'reservable' : '';
  }, [searchData]);

  const onSubmit = () => {
    if (!isValid) {
      setShowValidation(true);
      return;
    }

    const isMonthlyBooking = searchData.bookingType !== 'hourly';

    gtagTrack('core:search-block:clicked-search', { isMonthlyBooking });

    const reservableFilter = filterQueryString
      ? `&filters=${filterQueryString}`
      : '';

    const { arriving, leaving, monthlyStarting, monthlyDuration } = searchData;

    const arrivingValue =
      arriving || defaultDates.arriving.format(localDateTimeIso8601);

    const leavingValue =
      leaving || defaultDates.leaving.format(localDateTimeIso8601);
    const monthlyStartingValue =
      monthlyStarting ||
      defaultDates.arriving.startOf('day').format(localDateTimeIso8601);
    const latValue = searchData.searchData.lat || defaultLocation?.lat;
    const lngValue = searchData.searchData.lng || defaultLocation?.lng;
    const queryValue = searchData.query || placeholder;

    const dateQuery =
      searchData.bookingType === 'hourly'
        ? `&arriving=${arrivingValue}&leaving=${leavingValue}`
        : `&arriving=${monthlyStartingValue}&monthly_duration=0&monthly_type=${monthlyDuration}`;

    navigate({
      pathname: '/search',
      search: `?${dateQuery}${
        queryValue ? `&q=${queryValue}` : ''
      }&coords=${latValue},${lngValue}${
        searchData.searchData.googlePlaceId
          ? `&place_id=${searchData.searchData.googlePlaceId}`
          : ''
      }${reservableFilter}`
    });
  };

  const [getQuery] = useLazyQuery(GET_SEARCH);

  const disablePrefetch = useDarkly('disable-search-prefetch');

  useEffect(() => {
    if (isFormComplete && !disablePrefetch) {
      const {
        arriving,
        leaving,
        bookingType,
        monthlyStarting,
        monthlyDuration,
        query
      } = searchData;

      const dateDiff = calculateDateDiff(arriving, leaving);

      const { lat, lng, googlePlaceId } = searchData.searchData;

      const userQuery = query || placeholder;

      getQuery({
        variables: {
          lat,
          lng,
          startDate: bookingType === 'monthly' ? monthlyStarting : arriving,
          endDate: leaving,
          monthlyApplicableDays:
            bookingType === 'monthly' ? monthlyDuration : undefined,
          googlePlaceId,
          userQuery,
          filters: filterQueryString || null
        },
        skip: dateDiff >= 90,
        ssr: false,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: false
      });
    }
  }, [
    searchData,
    filterQueryString,
    isFormComplete,
    getQuery,
    placeholder,
    disablePrefetch
  ]);

  const driveupListingId = searchData.searchData?.listingId;

  useEffect(() => {
    if (driveupListingId) {
      navigate({
        pathname: `/checkout/${driveupListingId}`
      });
    }
  }, [driveupListingId, navigate]);

  useEffect(() => {
    setShowValidation(false);
  }, [searchData.bookingType]);

  const setDefaultHourlyDates = useCallback(() => {
    setSearchData((existingData) => ({
      ...existingData,
      arriving: defaultSearchDates().arriving.format(localDateTimeIso8601),
      leaving: defaultSearchDates().leaving.format(localDateTimeIso8601)
    }));
  }, []);

  const setDefaultMonthlyDates = useCallback(() => {
    setSearchData((existingData) => ({
      ...existingData,
      monthlyStarting: defaultSearchDates()
        .arriving.startOf('day')
        .format(localDateTimeIso8601)
    }));
  }, []);

  const placeholderText = isDriveup
    ? t('searchForm:driveupPlaceholder', 'Enter location ID')
    : placeholder;

  const geoLocationChange = useCallback(
    (address, coordinates) => {
      dispatch(setGeolocation(address, coordinates));
    },
    [dispatch]
  );

  const recentSearchesRef = useRef(recentSearches);
  recentSearchesRef.current = recentSearches;

  const handleOnLocationChange = useCallback(
    (result) => {
      const existsAsFavourite = favourites.some(
        (item) =>
          item.searchData.googlePlaceId === result.searchData.googlePlaceId
      );

      if (!showFamiliarSpaces || existsAsFavourite) return;

      const newRecentSearches = handleFamiliarSpaces(
        recentSearchesRef.current,
        result,
        5
      );

      setRecentSearches(newRecentSearches);
    },
    [favourites, recentSearchesRef, setRecentSearches, showFamiliarSpaces]
  );

  return (
    <div className={styles.base}>
      <SearchForm
        onChange={setSearchData}
        query={searchData.query}
        coords={coords}
        arriving={searchData.arriving}
        leaving={searchData.leaving}
        monthlyStarting={searchData.monthlyStarting}
        monthlyDuration={searchData.monthlyDuration}
        bookingType={searchData.bookingType}
        searchInputRef={searchInputRef}
        handleOnLocationChange={handleOnLocationChange}
        geoLocationChange={geoLocationChange}
        showValidation={showValidation}
        setDefaultHourlyDates={setDefaultHourlyDates}
        setDefaultMonthlyDates={setDefaultMonthlyDates}
        label={label}
        placeholder={placeholderText}
        locationBias={defaultLocation}
        resultsSlotComponent={
          showFamiliarSpaces && (
            <FavouriteSearches
              setSearchData={setSearchData}
              favourites={favourites}
              setFavourites={setFavourites}
              recentSearches={recentSearches}
              setRecentSearches={setRecentSearches}
            />
          )
        }
        isMonthlyDefault={isMonthlyDefault}
        t={t}
      />
      <SearchTrust
        className={styles.trust}
        leftText={t('search:trustSpaces', 'Spaces verified')}
        centralText={t('search:trustCancel', 'Easy to cancel')}
        rightText={t('search:trustExtend', 'Easy to extend')}
      />

      <Button
        className={styles.submitButton}
        onClick={onSubmit}
        cypress={`${cypress}-button`}
      >
        {submitText ||
          t('searchForm:submitText', 'Show me the best parking deals')}
      </Button>
    </div>
  );
};

export default SearchFormModule;
